import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  AbstractComponent,
  ChatService,
  ChatUserDisplay,
  ChatUserDisplayMap,
} from 'lib-trend-core';
import { Subject, combineLatest, interval, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import * as ChatActions from '../state/chat.actions';
import { presenceSelector, statusesSelector, userDisplaySelector } from '../state/chat.selectors';
import { ChatStatusEntry } from '../state/chat.state';
import { ChatPresenceView, resolvePresenceView } from './chat-status.util';
import { resolveChatInitials } from '../chat-initials.util';

/** Minimum term length before the company directory is queried. */
const DIRECTORY_MIN_TERM = 2;

/** How often the list re-evaluates status expiry (AC-25 auto-clear). */
const EXPIRY_TICK_MS = 30_000;

/** One colleague row — precomputed on data arrival (no per-CD work). */
export interface ChatTeamRow {
  id: string;
  name: string;
  image?: string;
  initial: string;
  isSelf: boolean;
  view: ChatPresenceView;
}

/**
 * Team status view (SPEC-2026-07-internal-chat, T-21 · RF-19/RF-29 · AC-16/AC-25)
 * — the "Equipe" pane of the `/chat` workspace.
 *
 * Lists colleagues with avatar, presence dot, availability label and custom
 * status, composed from the store (`userDisplay` + `presence` + `statuses`).
 * The bootstrap `userDisplay` map only covers people present in my visible
 * conversations, so typing 2+ characters also queries the membership-scoped
 * company directory (`GET /api/chat/search` → `people`) and merges the results
 * into the shared display map — the same source the people picker uses.
 */
@Component({
  selector: 'chat-team-status',
  templateUrl: './chat-team-status.component.html',
  styleUrls: ['./chat-team-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatTeamStatusComponent extends AbstractComponent implements OnInit {

  rows: ChatTeamRow[] = [];
  onlineCount = 0;
  searchTerm = '';
  directoryLoading = false;

  private readonly searchSubject = new Subject<string>();

  private userDisplay: ChatUserDisplayMap = {};
  private presence: Record<string, boolean> = {};
  private statuses: Record<string, ChatStatusEntry> = {};
  /** People resolved by the directory search but absent from the store map. */
  private extraDisplay: ChatUserDisplayMap = {};

  constructor(
    injector: Injector,
    private readonly store: Store,
    private readonly chatService: ChatService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(userDisplaySelector),
      this.store.select(presenceSelector),
      this.store.select(statusesSelector),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([userDisplay, presence, statuses]) => {
        this.userDisplay = userDisplay;
        this.presence = presence;
        this.statuses = statuses;
        this.rebuild();
      });

    // Company directory lookup — debounced, membership-scoped server-side.
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term.length < DIRECTORY_MIN_TERM) {
            this.directoryLoading = false;
            return of(null);
          }
          this.directoryLoading = true;
          return this.chatService.search({ q: term, limit: 25 })
            .pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((envelope) => {
        this.directoryLoading = false;
        const people = envelope?.data?.people ?? [];
        if (people.length === 0) {
          this.rebuild();
          return;
        }
        const resolved: ChatUserDisplayMap = {};
        people.forEach((person) => {
          resolved[person._id] = { name: person.name, image: person.image } as ChatUserDisplay;
        });
        this.extraDisplay = { ...this.extraDisplay, ...resolved };
        // Share the resolution with every other chat surface (merge only).
        this.store.dispatch(ChatActions.mergeUserDisplay({ userDisplay: resolved }));
        this.rebuild();
      });

    interval(EXPIRY_TICK_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.rebuild());
  }

  onSearch(): void {
    this.searchSubject.next(this.searchTerm.trim().toLowerCase());
    this.rebuild();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchSubject.next('');
    this.rebuild();
  }

  trackRow(_index: number, row: ChatTeamRow): string {
    return row.id;
  }

  // ---------------------------------------------------------------------------
  // View-model building
  // ---------------------------------------------------------------------------

  private rebuild(): void {
    const now = Date.now();
    const myId = this.getIDCurrentUser();
    const display: ChatUserDisplayMap = { ...this.extraDisplay, ...this.userDisplay };

    // Presence/status maps can carry company colleagues the display map has not
    // resolved yet — they are kept out rather than rendered as "?". Inactive
    // entries (removed/blocked/off-company — flagged by the bootstrap) are not
    // part of the team either: their names only exist to label old messages.
    const rows: ChatTeamRow[] = Object.keys(display)
      .filter((id) => !!display[id]?.name)
      .filter((id) => !display[id]?.inactive)
      .map((id) => ({
        id,
        name: display[id].name,
        image: display[id].image,
        initial: resolveChatInitials(display[id].name || '?'),
        isSelf: id === myId,
        view: resolvePresenceView(
          this.statuses[id],
          id === myId ? (this.presence[id] ?? true) : !!this.presence[id],
          now,
        ),
      }));

    const term = this.searchTerm.trim().toLowerCase();
    const filtered = term ? rows.filter((row) => row.name.toLowerCase().includes(term)) : rows;

    // Online first, then alphabetical — myself always on top.
    this.rows = filtered.sort((a, b) => {
      if (a.isSelf !== b.isSelf) return a.isSelf ? -1 : 1;
      if (a.view.online !== b.view.online) return a.view.online ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    this.onlineCount = rows.filter((row) => row.view.online).length;
  }
}
